/**
 * Helpers for changing one record in a list without re-fetching the whole
 * list. No helper mutates its input. When a call changes something it returns
 * a new array (or meta object). When it changes nothing it returns the input
 * itself, so the result can always be assigned straight back onto reactive
 * state without a spurious update.
 */

/**
 * Read an item's identity, either from a property or from a function.
 *
 * @param {object} item
 * @param {string|function(object): *} key
 * @returns {*}
 */
function identityOf(item, key) {
    if (typeof key === 'function') {
        return key(item)
    }

    return item == null ? undefined : Reflect.get(item, key)
}

/**
 * Find the index of the item with the given identity. A null or undefined
 * identity never matches, so items without one are not mistaken for each
 * other.
 *
 * @param {Array<object>} list
 * @param {*} keyValue
 * @param {string|function(object): *} key
 * @returns {number} The index, or -1 when nothing matches
 */
function indexOfKey(list, keyValue, key) {
    if (keyValue === null || keyValue === undefined) {
        return -1
    }

    return list.findIndex((item) => identityOf(item, key) === keyValue)
}

/**
 * Replace the item with the same identity, or insert it when the list does
 * not have it yet.
 *
 * With `position: 'sorted'` and a `compare` function, the item is placed in
 * sorted order, so a renamed item moves to its new place. Otherwise an
 * existing item is replaced where it sits, and a new item goes to the start
 * or end of the list.
 *
 * @param {Array<object>} list
 * @param {object} item
 * @param {string|function(object): *} [key='ulid'] Property holding the
 *     identity, or a function that returns it
 * @param {object} [options]
 * @param {'append'|'prepend'|'sorted'} [options.position='append']
 * @param {function(object, object): number} [options.compare] Comparator used
 *     when position is 'sorted'
 * @returns {Array<object>}
 */
export function upsertBy(
    list,
    item,
    key = 'ulid',
    { position = 'append', compare } = {}
) {
    const index = indexOfKey(list, identityOf(item, key), key)

    if (position === 'sorted' && compare) {
        const rest = index === -1 ? list : list.filter((_, i) => i !== index)
        const at = rest.findIndex((existing) => compare(item, existing) < 0)

        return at === -1
            ? [...rest, item]
            : [...rest.slice(0, at), item, ...rest.slice(at)]
    }

    if (index !== -1) {
        return [...list.slice(0, index), item, ...list.slice(index + 1)]
    }

    return position === 'prepend' ? [item, ...list] : [...list, item]
}

/**
 * Remove the item with the given identity. A list without a match comes
 * back as the same array, not a copy.
 *
 * @param {Array<object>} list
 * @param {*} keyValue
 * @param {string|function(object): *} [key='ulid']
 * @returns {Array<object>}
 */
export function removeBy(list, keyValue, key = 'ulid') {
    if (keyValue === null || keyValue === undefined) {
        return list
    }

    if (indexOfKey(list, keyValue, key) === -1) {
        return list
    }

    return list.filter((item) => identityOf(item, key) !== keyValue)
}

/**
 * Shift a pagination `meta.total` by a delta after an insert or remove. The
 * total never goes below zero. Meta without a numeric total is returned as is.
 *
 * @param {object|null|undefined} meta
 * @param {number} delta
 * @returns {object|null|undefined}
 */
export function adjustTotal(meta, delta) {
    if (!meta || typeof meta.total !== 'number') {
        return meta
    }

    return { ...meta, total: Math.max(0, meta.total + delta) }
}

/**
 * Cut a page back to its page size. Use it after an insert, which can push
 * the page over `per_page`. A page at or under the limit comes back unchanged.
 *
 * @param {Array<object>} list
 * @param {number} perPage
 * @returns {Array<object>}
 */
export function trimToPerPage(list, perPage) {
    if (!(perPage > 0) || list.length <= perPage) {
        return list
    }

    return list.slice(0, perPage)
}
